import React, { useEffect } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AGGREGATIONS,
  BEHAVIORS,
  DATA_TYPES,
  EVENTS,
  GOALS,
  NO_VALUE,
  REFRESH,
  TRAIT_TYPES,
  TraitConfig,
  WINDOWS,
} from "../data";
import { useTraitSources } from "../api";
import { Card, Field, SectionHeader, SelectBox, ghostBtn, inputCls, primaryBtn } from "./ui";

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">{children}</div>;

const AVAILABLE_IN: { key: keyof TraitConfig["availableIn"]; label: string }[] = [
  { key: "segments", label: "Segments" },
  { key: "journeys", label: "Journeys" },
  { key: "campaigns", label: "Campaigns" },
];

const StepConfigure: React.FC<{
  config: TraitConfig;
  update: (patch: Partial<TraitConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}> = ({ config, update, onBack, onNext }) => {
  const type = TRAIT_TYPES.find((t) => t.category === config.category)!;

  // Source System/Table/Field come from GET /traits/sources for Custom
  // traits — key_fields are only documented per source system (not per
  // table), so the Field dropdown doesn't change when the table does.
  const { data: sources, loading: sourcesLoading } = useTraitSources();
  const sourceList = sources?.data || [];
  const sourceSystems = sourceList.map((s) => s.source_system);
  const currentSource = sourceList.find((s) => s.source_system === config.sourceSystem);
  const tables = currentSource?.warehouse_tables || [];
  const fields = currentSource?.key_fields || ["value"];

  useEffect(() => {
    if (config.category !== "Custom" || !sourceList.length) return;
    if (sourceList.some((s) => s.source_system === config.sourceSystem)) return;
    const first = sourceList[0];
    update({ sourceSystem: first.source_system, table: first.warehouse_tables[0] || "", field: first.key_fields[0] || "value" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceList, config.category]);

  const setEnum = (i: number, v: string) => update({ enumValues: config.enumValues.map((x, idx) => (idx === i ? v : x)) });

  const next = () => {
    if (!config.name.trim()) {
      toast.error("Give the trait a name first.");
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      <SectionHeader title={`Configure ${type.buildTitle}`} subtitle="Start by selecting the type of trait you want to create. You can configure the details in the next step" />

      <Card title="Basic Details" subtitle="Add basic details of this trait such as trait name, label and a description">
        <div className="space-y-4">
          <Grid>
            <Field label="Trait Name">
              <input value={config.name} onChange={(e) => update({ name: e.target.value.replace(/\s+/g, "_").toLowerCase() })} className={inputCls} />
            </Field>
            <Field label="Display Label">
              <input value={config.label} onChange={(e) => update({ label: e.target.value })} className={inputCls} />
            </Field>
          </Grid>
          <Field label="Description">
            <input value={config.description} onChange={(e) => update({ description: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Data Type" info="How values of this trait are stored and compared">
            <SelectBox value={config.dataType} onChange={(v) => update({ dataType: v })} options={DATA_TYPES} />
          </Field>
          {config.dataType === "Enum" && (
            <div>
              <p className="text-base text-gray-800 dark:text-gray-200">Enter Enum Values</p>
              <div className="mt-2 space-y-3">
                {config.enumValues.map((v, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <input value={v} onChange={(e) => setEnum(i, e.target.value)} className={inputCls} />
                    <button
                      type="button"
                      title="Remove value"
                      onClick={() => update({ enumValues: config.enumValues.filter((_, idx) => idx !== i) })}
                      className="text-rose-400 hover:text-rose-600 shrink-0"
                    >
                      <Trash2 size={22} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => update({ enumValues: [...config.enumValues, ""] })} className="mt-3 text-base text-indigo-700 dark:text-indigo-400 underline underline-offset-2">
                Add New
              </button>
            </div>
          )}
        </div>
      </Card>

      {config.category === "Custom" && (
        <Card
          title="Source Mapping"
          subtitle={sourcesLoading ? undefined : "Map the trait sources to customer tables and behaviors"}
        >
          {sourcesLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading sources…
            </p>
          ) : (
            <Grid>
              <Field label="Source System">
                <SelectBox
                  value={config.sourceSystem}
                  onChange={(v) => {
                    const match = sourceList.find((s) => s.source_system === v);
                    const t = match?.warehouse_tables[0] || "";
                    update({ sourceSystem: v, table: t, field: match?.key_fields[0] || "value" });
                  }}
                  options={sourceSystems}
                />
              </Field>
              <Field label="Object/ Table">
                <SelectBox value={config.table} onChange={(v) => update({ table: v })} options={tables} />
              </Field>
              <Field label="Source Field">
                <SelectBox value={config.field} onChange={(v) => update({ field: v })} options={fields} />
              </Field>
              <Field label="Behavior">
                <SelectBox value={config.behavior} onChange={(v) => update({ behavior: v })} options={BEHAVIORS} />
              </Field>
            </Grid>
          )}
        </Card>
      )}

      {config.category === "Behavioral" && (
        <Card title="Logic" subtitle="Define how this trait is calculated from customer activity">
          <Grid>
            <Field label="Event">
              <SelectBox value={config.event} onChange={(v) => update({ event: v })} options={EVENTS} />
            </Field>
            <Field label="Calculation">
              <SelectBox value={config.aggregation} onChange={(v) => update({ aggregation: v })} options={AGGREGATIONS} />
            </Field>
            <Field label="Time Window">
              <SelectBox value={config.window} onChange={(v) => update({ window: v })} options={WINDOWS} />
            </Field>
            <Field label="Behavior">
              <SelectBox value={config.behavior} onChange={(v) => update({ behavior: v })} options={BEHAVIORS} />
            </Field>
          </Grid>
        </Card>
      )}

      {config.category === "Predictive" && (
        <Card title="Prediction" subtitle="Choose what ConvoAI should predict and how often it refreshes">
          <Grid>
            <Field label="Prediction Goal">
              <SelectBox value={config.goal} onChange={(v) => update({ goal: v })} options={GOALS} />
            </Field>
            <Field label="Refresh">
              <SelectBox value={config.refresh} onChange={(v) => update({ refresh: v })} options={REFRESH} />
            </Field>
          </Grid>
        </Card>
      )}

      <Card title="Additional Settings" subtitle="Configure what the CRM should do when no value exists">
        <div className="space-y-4">
          <Grid>
            <Field label="When no value exists">
              <SelectBox value={config.noValue} onChange={(v) => update({ noValue: v })} options={NO_VALUE} />
            </Field>
            {config.noValue === "Use a default value" && (
              <Field label="Default Value">
                <input value={config.defaultValue} onChange={(e) => update({ defaultValue: e.target.value })} className={inputCls} />
              </Field>
            )}
          </Grid>
          <div>
            <p className="text-base text-gray-800 dark:text-gray-200">Make the trait available in</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {AVAILABLE_IN.map((a) => (
                <label key={a.key} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-base text-gray-700 dark:text-gray-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.availableIn[a.key]}
                    onChange={(e) => update({ availableIn: { ...config.availableIn, [a.key]: e.target.checked } })}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  {a.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <button type="button" className={ghostBtn} onClick={onBack}>
          Back to Selection
        </button>
        <button type="button" className={primaryBtn} onClick={next}>
          Preview
        </button>
      </div>
    </div>
  );
};

export default StepConfigure;
